import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState(i18n.language || "ja");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleLang = () => {
    const next = lang === "ja" ? "en" : "ja";
    i18n.changeLanguage(next);
    setLang(next);
  };

  const navLinks = [
    { key: "nav.top", path: "/" },
    { key: "nav.service", path: "/service" },
    { key: "nav.system", path: "/system" },
    { key: "nav.flow", path: "/introduction-flow" },
    { key: "nav.about", path: "/about" },
    { key: "nav.contact", path: "/contact" },
  ];

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/9051b913-a4fc-49c8-8d5f-b0cc0fd3b0da.png"
            alt="Stream Solutions Limited"
            className="h-8 lg:h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              to={link.path}
              className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                isActive(link.path)
                  ? scrolled
                    ? "text-cyan-600"
                    : "text-cyan-300"
                  : scrolled
                  ? "text-slate-700 hover:text-cyan-600"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {t(link.key)}
              {isActive(link.path) && (
                <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-cyan-500 rounded-full" />
              )}
            </Link>
          ))}
        </nav>

        {/* Right: Lang + CTA */}
        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={toggleLang}
            className={`text-xs font-semibold tracking-widest px-3 py-1.5 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              scrolled
                ? "border-slate-300 text-slate-600 hover:border-cyan-500 hover:text-cyan-600"
                : "border-white/40 text-white/80 hover:border-white hover:text-white"
            }`}
          >
            {lang === "ja" ? "EN" : "JA"}
          </button>
          <Link
            to="/contact"
            className="px-5 py-2 text-sm font-semibold rounded-full bg-cyan-500 text-white hover:bg-cyan-400 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {t("nav.contact")}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="flex lg:hidden items-center gap-3">
          <button
            onClick={toggleLang}
            className={`text-xs font-semibold tracking-widest px-2.5 py-1 rounded-full border transition-all duration-200 cursor-pointer ${
              scrolled
                ? "border-slate-300 text-slate-600"
                : "border-white/40 text-white/80"
            }`}
          >
            {lang === "ja" ? "EN" : "JA"}
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`w-8 h-8 flex flex-col items-center justify-center gap-1.5 cursor-pointer ${
              scrolled ? "text-slate-700" : "text-white"
            }`}
          >
            <span
              className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
                menuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
                menuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-5 h-0.5 bg-current transition-all duration-300 ${
                menuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden transition-all duration-300 overflow-hidden ${
          menuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        } bg-white/98 backdrop-blur-md border-t border-slate-100`}
      >
        <nav className="px-6 py-4 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.key}
              to={link.path}
              className={`px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 cursor-pointer ${
                isActive(link.path)
                  ? "text-cyan-600 bg-cyan-50"
                  : "text-slate-700 hover:text-cyan-600 hover:bg-slate-50"
              }`}
            >
              {t(link.key)}
            </Link>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
